import type { LucideIcon } from 'lucide-react';

export interface MenuItem {
  id: string;
  label: string;
  icon: LucideIcon;
  view: 'dashboard' | 'kanban' | 'gantt' | 'document' | 'settings' | 'docs' | 'reports' | 'calendar';
}

export interface FileSystemItem {
  name: string;
  type: 'file' | 'folder';
  expanded?: boolean;
  children?: FileSystemItem[];
  content?: string;
  lastModified?: string;
}

export interface DragState {
  isDragging: boolean;
  dragType: string | null;
  draggedElement: HTMLElement | null;
  mouseOffset: { x: number; y: number };
  ghostElement: HTMLElement | null;
  originalData?: Record<string, unknown>;
  resizePreviewDayIndex?: number;
}

// Gantt hierarchical node type
export interface GanttNode {
  id: string;
  type: 'task' | 'group' | 'milestone';
  title: string;
  parentId?: string;
  children?: GanttNode[];
  // Task specific
  startDate?: string;
  endDate?: string;
  progress?: number; // Progress percentage (0-100)
  // Milestone specific
  date?: string;
  // UI state
  expanded?: boolean;
}


export interface Month {
  name: string;
  days: number;
  startDate: Date;
}


// Operation Classification System
export enum OperationType {
  // Data Operations - ALWAYS sync to server
  DATA_MUTATION = 'data_mutation',
  DATA_BATCH = 'data_batch',
  DATA_IMPORT = 'data_import',
  
  // Collaborative UI - SELECTIVE sync
  COLLAB_VIEW = 'collab_view',
  COLLAB_SELECTION = 'collab_selection',
  
  // Local UI - NEVER sync to server
  LOCAL_SEARCH = 'local_search',
  LOCAL_FILTER = 'local_filter',
  LOCAL_NAVIGATION = 'local_navigation',
  LOCAL_PREFERENCE = 'local_preference',
  LOCAL_INTERACTION = 'local_interaction'
}

export interface OperationConfig {
  type: OperationType;
  sync: boolean;
  history: boolean;
}

export const OPERATION_CONFIG: Record<string, OperationConfig> = {
  // Data operations
  'create': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'update': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'delete': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'move': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'batch-update': { type: OperationType.DATA_BATCH, sync: true, history: true },
  'reorder-columns': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'reorder': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'add-column': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'update-column': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  'delete-column': { type: OperationType.DATA_MUTATION, sync: true, history: true },
  
  // Local UI operations (never sync to server)
  'search-change': { type: OperationType.LOCAL_SEARCH, sync: false, history: false },
  'filter-change': { type: OperationType.LOCAL_FILTER, sync: false, history: false },
  'view-change': { type: OperationType.LOCAL_NAVIGATION, sync: false, history: false },
  'toggle-expand': { type: OperationType.LOCAL_PREFERENCE, sync: false, history: false },
  'select-node': { type: OperationType.LOCAL_INTERACTION, sync: false, history: false },
  'hover-node': { type: OperationType.LOCAL_INTERACTION, sync: false, history: false },
  'drag-start': { type: OperationType.LOCAL_INTERACTION, sync: false, history: false },
  'drag-end': { type: OperationType.LOCAL_INTERACTION, sync: false, history: false },
  
  // Optional collaborative UI (currently disabled)
  'shared-selection': { type: OperationType.COLLAB_SELECTION, sync: false, history: false },
  'shared-view': { type: OperationType.COLLAB_VIEW, sync: false, history: false }
};

export * from './projectData';

// Re-export calendar types from features
export type { ViewMode, CalendarEvent } from '@/features/calendar/types';
